import React, { Component, Fragment } from "react";
import TooltipContainer from "../../../../libraries/TooltipContainer";
import { dataFormatters } from "../../../../utils/dataFormatters";
import { DateUtils } from "../../../../utils/dateUtils";
import "./style.css";
import moment from "moment";
import { languageService } from "../../../../Language/language.service";

function getAbbreviation(lookup = {}, name) {
  function getCurrVariant(abbreviation, variant) {
    return variant > 0 ? `${abbreviation}${variant}` : abbreviation;
  }

  if (lookup[name]) return lookup[name];

  let abbreviation = name.toUpperCase().replace(" ", "").substring(0, 4);
  let variant = 0;
  do {
    // create variant with counter value if required
  } while (Object.values(lookup).find((abbr) => getCurrVariant(abbreviation, variant) === abbr) && ++variant);

  return getCurrVariant(abbreviation, variant);
}

const filterRecordsWithMissingInformation = (reportingPeriod = []) => {
  let filteredRecords = reportingPeriod.filter((record) => {
    const { dutyType, serviceType, startDate, endDate } = record;
    return dutyType && startDate && endDate;
  });
  let records = [];
  for (let i = 0; i < filteredRecords.length; i++) {
    let prevRecord = i === 0 ? null : filteredRecords[i - 1];
    let currRecord = filteredRecords[i];
    let nextRecord = i === filteredRecords.length - 1 ? null : filteredRecords[i + 1];

    records.push({ ...currRecord });
    if (nextRecord) {
      let startDate = moment(currRecord.endDate).toDate().toISOString();
      let endDate = moment(nextRecord.startDate).toDate().toISOString();
      if (startDate !== endDate) {
        const offDays = splitDaysToDayArray(startDate, endDate);
        for (let j = 1; j < offDays.length; j++) {
          records.push(getOffDuty(offDays[j - 1], offDays[j]));
        }
      }
    }
  }
  return records;
};

export const SingleDayHosGraphicalView = ({ reportingPeriod, serviceDate }) => {
  if (!Array.isArray(reportingPeriod)) return null;

  const filledReportPeriod = filterRecordsWithMissingInformation(reportingPeriod);

  const TOTAL_SECONDS_IN_DAY = 24 * 60 * 60;
  const SECONDS_IN_HOUR = 60 * 60;
  const TOTAL_HOURS_IN_DAY = 24;
  const TOTAL_BINS_PER_HOUR = 60;
  const FIRST_COL_SPAN = 400;
  const LAST_COL_SPAN = 250;
  const HOURS_ARRAY_WITH_NUMBERS = Array.from(Array(TOTAL_HOURS_IN_DAY).keys());

  const totalColSpan = FIRST_COL_SPAN + TOTAL_BINS_PER_HOUR * TOTAL_HOURS_IN_DAY + LAST_COL_SPAN;
  const title = `Hours of Service${serviceDate ? ` - ${serviceDate}` : ""}`;

  // prepare data
  const hosGraphicalData = filledReportPeriod.reduce(
    (result, record) => {
      const { serviceType, dutyType } = record;
      if (serviceType === "Covered Service") {
        result[serviceType].records.push({ ...record, className: "covered-service" });
      } else if (serviceType === "Commingled Service") {
        result["Covered Service"].records.push({ ...record, className: "comingled-service" });
      } else if (serviceType === "Non-covered Service") {
        result[serviceType].records.push({ ...record, className: "non-covered-service" });
      } else if (serviceType === "Off Duty") {
        result[serviceType].records.push({ ...record, className: "off-duty" });
      } else if (dutyType === "Interrupted Rest") {
        result["Non-covered Service"].records.push({ ...record, className: "interrupted-rest" });
      }
       else if (serviceType === "Limbo") {
        result[serviceType].records.push({ ...record, className: "limbo-service" });
      }
      return result;
    },
    {
      "Covered Service": {
        records: [],
        hours: [],
      },
      "Non-covered Service": {
        records: [],
        hours: [],
      },
      "Off Duty": {
        records: [],
        hours: [],
        disableTooltip: true,
      },
    },
  );
  // const MAX_BINS_TO_COVER = TOTAL_HOURS_IN_DAY * TOTAL_BINS_PER_HOUR;

  for (const key in hosGraphicalData) {
    let prevSeconds = 0;
    hosGraphicalData[key].totalSeconds = hosGraphicalData[key].records
      .map((record) => DateUtils.getTimeDiffInMilliseconds(record.startDate, record.startTime, record.endDate, record.endTime))
      .filter((millis) => millis)
      .reduce((sum, millis) => millis + sum, 0);
    hosGraphicalData[key].totalTime = dataFormatters.formatTimeDuration(hosGraphicalData[key].totalSeconds, false);
    hosGraphicalData[key].hours =
      hosGraphicalData[key].records.length > 0
        ? hosGraphicalData[key].records
            .reduce((activities, activity, index, arr) => {
              const startSeconds =
                DateUtils.getTimeDiffInMilliseconds(activity.startDate, "00:00", activity.startDate, activity.startTime) / 1000;
              const endSeconds =
                DateUtils.getTimeDiffInMilliseconds(activity.startDate, "00:00", activity.endDate, activity.endTime) / 1000;
              startSeconds !== 0 && activities.push({ active: false, startSeconds: prevSeconds, endSeconds: startSeconds });
              activities.push({ active: true, startSeconds: startSeconds, endSeconds: endSeconds, ...activity });
              if (index + 1 === arr.length && endSeconds < TOTAL_SECONDS_IN_DAY) {
                // last item
                activities.push({ active: false, startSeconds: endSeconds, endSeconds: TOTAL_SECONDS_IN_DAY });
              }
              prevSeconds = endSeconds;
              return activities;
            }, [])
            .reduce((resultHours, activity) => {
              const { startSeconds, endSeconds, active, startTime, endTime, startLoc, endLoc, activityType, className, serviceType } =
                activity;
              let secs = startSeconds;
              if (active) {
                resultHours.push({
                  active: true,
                  colSpan: (endSeconds - startSeconds) / (SECONDS_IN_HOUR / TOTAL_BINS_PER_HOUR), // add hour
                  startTime: startTime,
                  endTime: endTime,
                  startLoc: startLoc,
                  endLoc: endLoc,
                  activityType: activityType,
                  className: className,
                  serviceType: serviceType,
                });
              } else {
                while (secs < endSeconds && secs + SECONDS_IN_HOUR <= endSeconds) {
                  let bins = TOTAL_BINS_PER_HOUR;
                  if (secs % SECONDS_IN_HOUR !== 0) {
                    const remainder = SECONDS_IN_HOUR - (secs % SECONDS_IN_HOUR);
                    bins = (TOTAL_BINS_PER_HOUR * remainder) / SECONDS_IN_HOUR;
                    secs += remainder;
                  } else {
                    secs += (bins * SECONDS_IN_HOUR) / TOTAL_BINS_PER_HOUR;
                  }
                  resultHours.push({
                    active: false,
                    colSpan: bins, // add hour
                  });
                }
                if (secs < endSeconds) {
                  let bins = TOTAL_BINS_PER_HOUR;
                  const remainder = endSeconds - secs;

                  bins = (TOTAL_BINS_PER_HOUR * remainder) / SECONDS_IN_HOUR;
                  secs += remainder;

                  resultHours.push({
                    active: false,
                    colSpan: bins, // add hour
                  });
                }
              }

              return resultHours;
            }, [])
        : HOURS_ARRAY_WITH_NUMBERS.map((hour) => ({
            active: false,
            colSpan: TOTAL_BINS_PER_HOUR, // add hour
          }));
  }

  let abbreviationLookup = {};
  return (
    <div className={"hos-graphical-view-wrapper"}>
      <div className="no-print" style={{ flex: "auto" }}></div>
      <div className="table-bordered hos-graphical-view" style={{ flex: 8 }}>
        {/* <thead> */}
        <div className="titles border-bottom " style={{ display: "flex", height: "35px" }}>
          <div className={`tbl-bordered-header text-center bold-text cell-center`} style={{ flex: totalColSpan }}>
            {title}
          </div>
        </div>
        <div style={{ display: "flex", height: "35px" }}>
          <div
            className={`titles cell-left bold-text border-bottom tbl-bordered-header text-left pd-left-5px rect-servicetype`}
            style={{ flex: FIRST_COL_SPAN }}
          >
            Service Type
          </div>
          {HOURS_ARRAY_WITH_NUMBERS.map((hour, i) => {
            return (
              <div
                key={i}
                className="border-bottom cell-center bold-text alternate-col tbl-bordered-header text-center square"
                style={{ flex: TOTAL_BINS_PER_HOUR }}
              >
                {dataFormatters.padZerosBeforeNum(hour, 2)}
              </div>
            );
          })}
          <div
            className="titles cell-center bold-text border-bottom tbl-bordered-header text-center rect-totaltime"
            style={{ flex: LAST_COL_SPAN }}
          >
            Total Time
          </div>
        </div>
        {/* </thead> */}
        {/* <tbody> */}
        {Object.keys(hosGraphicalData).map((serviceType, i) => {
          const { totalTime, hours, disableTooltip } = hosGraphicalData[serviceType];
          return (
            <div key={i} style={{ display: "flex", height: "35px" }} className="service-row">
              <div className="titles cell-left bold-text pd-left-5px rect-servicetype" style={{ flex: FIRST_COL_SPAN }}>
                {serviceType}
              </div>
              {hours.map((hour, index) => {
                const className = hour.className ? hour.className : "";
                const startTime = hour.startTime ? hour.startTime : "-";
                const endTime = hour.endTime ? hour.endTime : "-";
                const startLoc = hour.startLoc && hour.startLoc.description ? hour.startLoc.description : "-";
                const endLoc = hour.endLoc && hour.endLoc.description ? hour.endLoc.description : "-";

                let abbreviatedStartLoc = "";
                let abbreviatedEndLoc = "";
                if (hour.active == true) {
                  abbreviatedStartLoc = getAbbreviation(abbreviationLookup, startLoc);
                  hour.startLoc && startLoc !== "-" && (abbreviationLookup[startLoc] = abbreviatedStartLoc);
                  abbreviatedEndLoc = getAbbreviation(abbreviationLookup, endLoc);
                  hour.endLoc && endLoc !== "-" && (abbreviationLookup[endLoc] = abbreviatedEndLoc);
                }
                return (
                  <div
                    key={`hour-${index}`}
                    style={{ flex: hour.colSpan, position: "relative" }}
                    className={`light-border-bottom cell-center text-center ${
                      hour.active ? `${disableTooltip ? "" : "active"} ${className}` : ""
                    }`}
                  >
                    {hour.active && !disableTooltip ? (
                      <Fragment>
                        <div>
                          {hour.endLoc ? (
                            <Fragment>
                              <div className="hos-entry-end-loc">{abbreviatedEndLoc}</div>
                              <div className="hos-entry-end-loc-line"></div>
                            </Fragment>
                          ) : null}
                          {hour.startLoc ? (
                            <Fragment>
                              <div className="hos-entry-start-loc">{abbreviatedStartLoc}</div>
                              <div className="hos-entry-start-loc-line"></div>
                            </Fragment>
                          ) : null}
                        </div>
                        <TooltipContainer
                          className="hos-graph-tooltip"
                          maxHeight
                          maxWidth
                          tooltipText={
                            <table className="hos-graph-tooltip-table">
                              <tbody>
                                {[
                                  { head: "Service Type", val: hour.serviceType },
                                  { head: "Activity Type", val: hour.activityType && hour.activityType.value },
                                  { head: "Start Time", val: startTime },
                                  { head: "End Time", val: endTime === "00:00" ? "24:00" : endTime },
                                  { head: "Start Location", val: startLoc },
                                  { head: "End Location", val: endLoc },
                                ].map((row) => (
                                  <tr>
                                    <td className="title text-left">{`${row.head}:`}</td>
                                    <td className="value text-left">{row.val}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          }
                        >
                          <div className={`bold-text hos-activity-area-box ${className}`}>
                            {hour.activityType && hour.activityType.code}
                          </div>
                        </TooltipContainer>
                      </Fragment>
                    ) : (
                      hour.activityType && hour.activityType.code
                    )}
                  </div>
                );
              })}
              <div className="titles cell-center bold-text text-center rect-totaltime" style={{ flex: LAST_COL_SPAN }}>
                {totalTime}
              </div>
            </div>
          );
        })}

        {/* show abbreviations and locations conditionally */}
        {Object.keys(abbreviationLookup).length > 0 && (
          <Fragment>
            <div style={{ display: "flex", height: "35px" }}></div>
            <div style={{ display: "flex", minHeight: "20px", marginTop: "22px" }}>
              <div style={{ display: "flex" }} className={`text-left`}>
                <span className="pd-left-10px bold-text">{`${languageService("LEGEND")}: `}&nbsp;&nbsp;</span>
                <span className="pd-left-10px">
                  {Object.keys(abbreviationLookup).map((loc, index, arr) => (
                    <Fragment>{`${abbreviationLookup[loc]} = ${loc}${index < arr.length - 1 ? "," : ""}`}&nbsp;&nbsp;&nbsp;&nbsp;</Fragment>
                  ))}
                </span>
              </div>
            </div>
          </Fragment>
        )}
      </div>
      <div className="no-print" style={{ flex: "auto" }}></div>
    </div>
  );
};

const addDayIfMissing = (days, day) => {
  if (!days[day]) {
    days[day] = {
      serviceDate: day,
      reportingPeriod: [],
    };
  }
  return days;
};

const splitDaysToDayArray = function (startDate, endDate) {
  let dates = [];

  let currDate = moment(startDate);
  let lastDate = moment(endDate);

  // todo: refactor this logic and fix the days splitting logic
  while (lastDate.diff(currDate) >= 86400000) {
    // greater than day
    dates.push(currDate.clone());
    currDate.add("1", "day");
  }

  if (lastDate.diff(currDate) !== 0) {
    dates.push(currDate.clone());
  }
  dates.push(lastDate.clone());
  return dates;
};

const enumerateDaysBetweenDates = function (startDate, endDate) {
  let dates = [];

  let currDate = moment(startDate).startOf("day");
  let lastDate = moment(endDate).startOf("day");

  while (currDate.isSameOrBefore(lastDate)) {
    dates.push(dataFormatters.stringifyDateInYYYYMMDD(currDate));
    currDate.add("1", "day");
  }

  return dates;
};

const getOffDuty = (startDate, endDate, startTime, endTime) => {
  let duty = {
    dutyType: "Off Duty",
    serviceType: "Off Duty",
    startDate: startDate,
    endDate: endDate,
  };
  if (startTime && endTime) {
    duty.startTime = startTime;
    duty.endTime = endTime;
  }
  return duty;
};

// splits the data greater than one day into multipe days and display accordingly
export const MultiDayHosGraphicalView = ({
  reportingPeriod,
  viewStartDate,
  timezone,
  singleDayViewOnly,
  forceFirstDate,
  addMode,
  templateNameConvention,
}) => {
  let startDateStr = viewStartDate && dataFormatters.stringifyDateInYYYYMMDD(viewStartDate);

  if (!Array.isArray(reportingPeriod))
    return <SingleDayHosGraphicalView key={startDateStr} reportingPeriod={[]} serviceDate={startDateStr} />;

  // filter records with missing information
  // force convert dates to moment
  const filledReportPeriod = filterRecordsWithMissingInformation(reportingPeriod).map((record) => ({
    ...record,
    startDate: moment.tz(record.startDate, record.timezone ? record.timezone : timezone),
    endDate: moment.tz(record.endDate, record.timezone ? record.timezone : timezone),
    // record.dutyType === "Interrupted Rest"
    //   ? moment.tz(record.startDate, timezone).clone().add(1, "minutes")
    //   :
  }));

  if (filledReportPeriod.length === 0)
    return (
      <SingleDayHosGraphicalView
        key={startDateStr}
        reportingPeriod={
          // display full day off duty
          singleDayViewOnly
            ? [
                getOffDuty(
                  startDateStr,
                  dataFormatters.stringifyDateInYYYYMMDD(moment.tz(viewStartDate, timezone).startOf("day").add(1, "day")),
                  "00:00",
                  "00:00",
                ),
              ]
            : []
        }
        serviceDate={templateNameConvention ? `Day 1` : startDateStr}
      />
    );

  const firstDate = forceFirstDate ? forceFirstDate : filledReportPeriod[0].startDate;
  const lastDate = filledReportPeriod[filledReportPeriod.length - 1].endDate;
  let days = enumerateDaysBetweenDates(firstDate, lastDate).reduce((days, day) => {
    days[day] = {
      serviceDate: day,
      reportingPeriod: [],
    };
    return days;
  }, {});

  // convert all dates to moment
  filledReportPeriod.forEach((record, index) => {
    const { startDate, endDate } = record;
    const startDay = dataFormatters.stringifyDateInYYYYMMDD(startDate);
    const endDay = dataFormatters.stringifyDateInYYYYMMDD(endDate);
    const startTime = dataFormatters.stringifyTimeIn24HourFormat(startDate);
    const endTime = dataFormatters.stringifyTimeIn24HourFormat(endDate);

    let dateTimes = {
      startDate: startDay,
      startTime: startTime,
      endDate: endDay,
      endTime: endTime,
    };

    if (index === 0 && startTime !== "00:00") {
      // Add off duty for the first day
      days[startDay].reportingPeriod.push(getOffDuty(startDay, startDay, "00:00", startTime));
    }

    if (startDay === endDay || endTime === "00:00") {
      days[startDay].reportingPeriod.push({ ...record, ...dateTimes, startDate: startDay });
    } else {
      // add both days separately
      days[startDay].reportingPeriod.push({ ...record, ...dateTimes, endTime: "00:00" });
      days[endDay].reportingPeriod.push({ ...record, ...dateTimes, startDate: endDay, startTime: "00:00" });
    }
  });

  let display = false;
  const renderedDays = Object.keys(days)
    .filter((day, index, _days_orig) => {
      if (singleDayViewOnly) {
        // check whether to display the day or not
        display = startDateStr === day;
        const lastRecord = days[day].reportingPeriod[days[day].reportingPeriod.length - 1];
        if (display && days[day]) {
          if (days[day].reportingPeriod.length > 0 && lastRecord.endTime !== "00:00") {
            // adding off duty for the remaining day
            days[day].reportingPeriod.push(
              getOffDuty(
                day,
                dataFormatters.stringifyDateInYYYYMMDD(
                  dataFormatters.getDateFromStringWithTimezone(day, lastRecord.timezone).startOf("day").add(1, "day"),
                ),
                lastRecord.endTime,
                "00:00",
              ),
            );
          }
        }
      } else if (display || startDateStr === day || !viewStartDate) {
        display = true;
        if (days[day].reportingPeriod.length === 0) {
          if (addMode && index === _days_orig.length - 1) {
            // do nothing for the last day of we are in add mode
          } else {
            days[day].reportingPeriod.push(
              getOffDuty(
                day,
                dataFormatters.stringifyDateInYYYYMMDD(
                  dataFormatters.getDateFromStringWithTimezone(day, timezone).startOf("day").add(1, "day"),
                ),
                "00:00",
                "00:00",
              ),
            );
          }
        }
      }
      return display;
    })
    .map((day, index) => (
      <SingleDayHosGraphicalView
        key={days[day].serviceDate}
        {...days[day]}
        serviceDate={templateNameConvention ? `Day ${index + 1}` : days[day].serviceDate}
      />
    ));

  return renderedDays.length === 0 ? (
    <SingleDayHosGraphicalView key={startDateStr} reportingPeriod={[]} serviceDate={startDateStr} />
  ) : (
    renderedDays
  );
};



// WEBPACK FOOTER //
// ./src/components/HoursOfService/Helper/HosGraphicalView/index.jsx